// src/components/IjrrGear/IjrrGearSidebar.js

import React from 'react';

/**
 * Sidebar component for filtering products by Category and Gender.
 * * @param {Array} categories - List of available categories from the API.
 * @param {Object} filters - Current active filters { categoryName: string, gender: string[] }.
 * @param {Function} onFilterChange - Callback to update the parent state.
 * @param {boolean} isSidebarOpen - State for mobile visibility.
 * @param {Function} setIsSidebarOpen - Toggle for mobile visibility.
 */
export default function IjrrGearSidebar({
    categories = [],
    filters,
    onFilterChange,
    isSidebarOpen,
    setIsSidebarOpen
}) {

    const categoriesToRender = Array.isArray(categories) ? categories : [];

    /**
     * Handles changes in gender checkboxes.
     * Fixed: Now uses the spread operator to avoid direct state mutation.
     */
    const handleGenderChange = (key) => {
        // Create a shallow copy of the array to maintain immutability
        let newGenders = [...filters.gender];
        const normalizedKey = key.toLowerCase();

        // If 'all' is selected, we clear the array (API default for all genders)
        if (normalizedKey === 'all') {
            onFilterChange('gender', []);
            closeSidebarOnMobile();
            return;
        }

        // Map 'na' to 'unisex' to match backend data structure
        const apiValue = normalizedKey === 'na' ? 'unisex' : normalizedKey;

        if (newGenders.includes(apiValue)) {
            // Remove the gender if it's already present
            newGenders = newGenders.filter(g => g !== apiValue);
        } else {
            // Add the new gender to the selection
            newGenders.push(apiValue);
        }

        onFilterChange('gender', newGenders);
        closeSidebarOnMobile();
    };

    /**
     * Handles changes in category radio buttons.
     */
    const handleCategoryChange = (name) => {
        onFilterChange('categoryName', name);
        closeSidebarOnMobile();
    };

    /**
     * Helper to close the sidebar after an action on mobile devices.
     */
    const closeSidebarOnMobile = () => {
        if (window.innerWidth < 1200) {
            setIsSidebarOpen(false);
        }
    };

    // Normalize current filters for easier comparison in the render method
    const normalizedGenderFilters = filters.gender.map(g => g.toLowerCase());

    const getGenderId = (key) => `gender-filter-${key}`;

    return (
        <div className={`innersidebar-filter cdx-scroll ${isSidebarOpen ? 'active' : 'hidden'} col-xl-2 col-12`}>
            <div className="close-filter" onClick={() => setIsSidebarOpen(false)}>
                <h5>close filter <i className="icofont-rounded-double-left"></i></h5>
            </div>

            {/* Gender Filter Section */}
            <div className="card">
                <div className="card-body">
                    <h6 className="filter-title">Gender</h6>
                    <ul className="filter-list cdx-scroll">
                        {['all', 'male', 'female', 'na'].map((key) => {
                            const apiValue = key === 'na' ? 'unisex' : key;
                            
                            // 'all' is checked if the array is empty
                            const isChecked = key === 'all'
                                ? normalizedGenderFilters.length === 0
                                : normalizedGenderFilters.includes(apiValue);

                            return (
                                <li key={key}>
                                    <div className="chek-group mb-8">
                                        <span className="custom-check-input">
                                            <input
                                                className="custom-input"
                                                type="checkbox"
                                                id={getGenderId(key)}
                                                name="gender"
                                                checked={isChecked}
                                                onChange={() => handleGenderChange(key)}
                                            />
                                            <label className="custom-input-label" htmlFor={getGenderId(key)}></label>
                                        </span>
                                        <label className="ml-5" htmlFor={getGenderId(key)}>
                                            {key === 'na'
                                                ? 'Unisex'
                                                : key.charAt(0).toUpperCase() + key.slice(1)}
                                        </label>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>

            {/* Category Filter Section */}
            <div className="card">
                <div className="card-body">
                    <h6 className="filter-title">Category</h6>
                    <ul className="filter-list cdx-scroll">
                        {categoriesToRender.map((cat) => (
                            <li key={cat.id}>
                                <div className="chek-group mb-8">
                                    <span className="custom-check-input">
                                        <input
                                            className="custom-input"
                                            type="radio"
                                            id={`category-${cat.id}`}
                                            name="category"
                                            checked={filters.categoryName === cat.name}
                                            onChange={() => handleCategoryChange(cat.name)}
                                        />
                                        <label className="custom-input-label" htmlFor={`category-${cat.id}`}></label>
                                    </span>
                                    <label className="ml-5" htmlFor={`category-${cat.id}`}>{cat.name}</label>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}